import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import '../../styles/pages/DeviceOverview.css';
import { useParams } from 'react-router-dom';
import {
  DeviceDashboardDto,
  DeviceReading,
  ParameterDto,
  ParameterDtoPreferred,
} from '../../services/deviceApi';
import { DevicesContext } from '../../contexts/DevicesContext';
import HelperFunctions, {
  timeRanges,
} from '../../utils/helperFunctions';
import { AlertCircle, ChevronDown, X } from 'lucide-react';
import {
  buildStyles,
  CircularProgressbarWithChildren,
} from 'react-circular-progressbar';
import { getDifferenceInHours, getLocalDateTimeString, getTimezoneAbbreviation, getUserTimezone } from '../../utils/timezoneUtils';
import { UserSelectedContext } from '../../contexts/UserSelectedContext';
import { fillMissingIntervals } from '../../utils/chartDataUtils';

function DeviceOverviewHighcharts({
  parametersForDevice,
  liveDeviceData,
  hidePollutantsCard = false,
}: {
  parametersForDevice: ParameterDtoPreferred[];
  liveDeviceData: DeviceDashboardDto;
  hidePollutantsCard?: boolean;
}) {

  // const [userSelectedParameterId, setSelectedParameterId] = useState<number>(0);
  // const [userSelectedParameterId2, setUserSelectedParameterId2] = useState<number>(0);
  const [chartData, setChartData] = useState<any[]>([]);
  const { id } = useParams<{ id: string }>();
  const [ShowSecondLine, setShowSecondLine] = useState(false);
  const [showTimeRangeDropdown, setShowTimeRangeDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [innerWidth, setInnerWidth] = useState(window.innerWidth);
  const isFetchingRef = useRef(false);
  const userPreferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
  const aqiStandardId = userPreferences.aqiStandardId || 1;
  const timeZone = userPreferences.timezone || getUserTimezone();
  const [customDateChanged, setCustomDateChanged] = useState(false);
  const [customDateError, setCustomDateError] = useState('');
  // Devices Context
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }
  const {
    loading,
    deviceSensorData,
    getDeviceSensorData,
    getLiveDataForDashboard
  } = devicesContext;

  // User Selected Context
  const userSelectedContext = useContext(UserSelectedContext);
  if (!userSelectedContext) {
    throw new Error('useUserSelected must be used within a UserSelectedProvider');
  }
  const {
    userSelectedParameterId,
    setUserSelectedParameterId,
    userSelectedParameterId2,
    setUserSelectedParameterId2,
    userSelectedTimeRange,
    setUserSelectedTimeRange,
    customFromDate,
    customToDate,
    setCustomFromDate,
    setCustomToDate,
  } = userSelectedContext;

  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  /* Use effect hook for getting theme and also it called themehandler when data theme attribute changes */
  useEffect(() => {
    const themeHadler = () => {
      const theme = document.documentElement.getAttribute("data-theme") as 'light' | 'dark' ?? null;
      if(theme) setTheme(theme);
    }

    themeHadler();

    const observer = new MutationObserver(themeHadler);

    observer.observe(document.documentElement,{
      attributes: true,
      attributeFilter: ["data-theme"]
    });

    return () => observer.disconnect();
  }, [])
  

  // Get Parameter Color  
  const GetParameterColor = (
    value: number,
    subIndex: number,
    parameterName: string
  ) => {
    if (
      parameterName === 'PM2.5' ||
      parameterName === 'PM10' ||
      parameterName === 'O₃' ||
      parameterName === 'CO' ||
      parameterName === 'NO₂'
    ) {
      if (subIndex === null) {
        return HelperFunctions.GetParameterColor(value, parameterName);
      } else {
        return HelperFunctions.GetParameterColor(subIndex, 'AQI');
      }
    } else {
      return HelperFunctions.GetParameterColor(value, parameterName);
    }
  };

  // Get Status Text
  const GetStatusText = (
    value: number,
    subIndex: number,
    parameterName: string
  ) => {
    if (
      parameterName === 'PM2.5' ||
      parameterName === 'PM10' ||
      parameterName === 'O₃' ||
      parameterName === 'CO' ||
      parameterName === 'NO₂'
    ) {
      if (subIndex === null) {
        return HelperFunctions.GetStatusText(value, parameterName);
      } else {
        return HelperFunctions.GetStatusText(subIndex, 'AQI');
      }
    } else {
      return HelperFunctions.GetStatusText(value, parameterName);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setInnerWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('.dropdown-container')) {
        setShowTimeRangeDropdown(false);
      }
    };

    if(isMobile) {
      document.removeEventListener('mousedown', handleClickOutside);
      return;
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

// Get the selected parameter id
  useEffect(() => {
    if (!parametersForDevice || parametersForDevice.length === 0) return;

    const validIds = parametersForDevice.map(p => p.driverID);

    // The selected parameter ids live in a shared context and persist across
    // devices. If the currently selected id isn't valid for THIS device (stale
    // value from a previously viewed device, or never set), fall back to AQI
    // when present, otherwise to the first available parameter.
    if (!validIds.includes(userSelectedParameterId)) {
      const aqiParam = parametersForDevice.find(p => p.parameterName === 'AQI');
      setUserSelectedParameterId(
        aqiParam ? aqiParam.driverID : parametersForDevice[0].driverID
      );
    }

    // Clear a stale second parameter that doesn't belong to this device.
    if (userSelectedParameterId2 !== 0 && !validIds.includes(userSelectedParameterId2)) {
      setUserSelectedParameterId2(0);
      setShowSecondLine(false);
    } else if (userSelectedParameterId2 !== 0) {
      setShowSecondLine(true);
    }

  }, [parametersForDevice]);


  // UseEffect to get sensor data from last updated value when device is office
  useEffect(() => {
    if (!id || !getDeviceSensorData || !liveDeviceData?.deviceId) return;
    setChartData([]);

    if (liveDeviceData.lastUpdated && !liveDeviceData.isActive) {
      isFetchingRef.current = true;

      const fromDate = new Date(new Date(liveDeviceData.lastUpdated).setHours(new Date(liveDeviceData.lastUpdated).getHours() - 12));
      const toDate = new Date(liveDeviceData.lastUpdated);

      setCustomFromDate(getLocalDateTimeString(fromDate));
      setCustomToDate(getLocalDateTimeString(toDate));
      setUserSelectedTimeRange('custom');

      getDeviceSensorData({
        DeviceId: Number(id),
        FromDate: fromDate.toISOString(),
        ToDate: toDate.toISOString(),
      }).finally(()=> {
        isFetchingRef.current = false;
      })
    }

    return () => {
      setCustomFromDate(getLocalDateTimeString(new Date(new Date().setDate(new Date().getDate() - 30))));
      setCustomToDate(getLocalDateTimeString(new Date()));
      setUserSelectedTimeRange("12h");
    }
  }, [])

  // API call when component mounts or time range changes
  useEffect(() => {
    if (!id || !getDeviceSensorData) return;

    if (userSelectedTimeRange === '12h') return;

    // Prevent duplicate API calls by using a ref
    if (isFetchingRef.current) return;

    setChartData([]);
    isFetchingRef.current = true;

    // Calculate date range based on selected time range
    let fromDate: string;
    let toDate: string = new Date().toISOString();
    if (userSelectedTimeRange === '12h') {
      fromDate = new Date(
        new Date().setHours(new Date().getHours() - 12)
      ).toISOString();
    } else if (userSelectedTimeRange === '24h') {
      fromDate = new Date(
        new Date().setDate(new Date().getDate() - 1)
      ).toISOString();
    } else if (userSelectedTimeRange === '7d') {
      fromDate = new Date(
        new Date().setDate(new Date().getDate() - 7)
      ).toISOString();
    } else if (userSelectedTimeRange === '30d') {
      fromDate = new Date(
        new Date().setDate(new Date().getDate() - 30)
      ).toISOString();
    } else if (userSelectedTimeRange === 'custom') {
      fromDate = new Date(customFromDate).toISOString();
      toDate = new Date(customToDate).toISOString();
    } else {
      fromDate = new Date(
        new Date().setHours(new Date().getHours() - 12)
      ).toISOString();
    }

    getDeviceSensorData({
      DeviceId: Number(id),
      FromDate: fromDate,
      ToDate: toDate,
    }).finally(() => {
      isFetchingRef.current = false;
    });

    // Mark initial load as complet
  }, [userSelectedTimeRange, customDateChanged]);

  //Custom date changes when user clicks on ok button
  const handleCustomDateChange = () => {
    if(!customFromDate || !customToDate) {
      setCustomDateError('Please select From and To dates');
      return;
    }
    
    // Extract date part (YYYY-MM-DD) and compare dates only, ignoring time
    // Handle both "YYYY-MM-DD" and "YYYY-MM-DDTHH:MM" formats
    const fromDateStr = customFromDate.includes('T') ? customFromDate.split('T')[0] : customFromDate;
    const toDateStr = customToDate.includes('T') ? customToDate.split('T')[0] : customToDate;
    
    // Compare date strings directly (YYYY-MM-DD format allows string comparison)
    if(fromDateStr >= toDateStr) {
      setCustomDateError('Start date must be before end date');
      return;
    }
    setCustomDateError('');
    setCustomDateChanged(!customDateChanged);
    setShowTimeRangeDropdown(false);
  };

  //Cancel custom date selection
  const handleCustomDateCancel = () => {
    setCustomDateError('');
    setShowTimeRangeDropdown(false);
  };

  // Live data updates ONLY when user is on 12h tab
  // Fetch immediately and then poll every 5 minutes
  useEffect(() => {
    if (!id || !getDeviceSensorData || !liveDeviceData?.deviceId) return;
    if (deviceSensorData && deviceSensorData.deviceId.toString() == id && liveDeviceData.lastUpdated === deviceSensorData.readings[deviceSensorData.readings.length - 1].recordedAt && liveDeviceData.isActive) return;

    if (userSelectedTimeRange !== '12h') return;

    // Prevent duplicate API calls by using a ref
    if (isFetchingRef.current) return;

    setChartData([]);
    isFetchingRef.current = true;


    getDeviceSensorData({
      DeviceId: Number(id),
      FromDate: new Date(new Date().setHours(new Date().getHours() - 12)).toISOString(),
      ToDate: new Date().toISOString(),
    }).finally(() => {
      isFetchingRef.current = false;
    });

  }, [liveDeviceData, userSelectedTimeRange]);
  

  useEffect(() => {
    if (!deviceSensorData?.readings || !deviceSensorData.readings.length)
      return;

    if (deviceSensorData.deviceId.toString() == id && liveDeviceData.lastUpdated !== deviceSensorData.readings[deviceSensorData.readings.length - 1].recordedAt && userSelectedTimeRange == '12h') {
      getLiveDataForDashboard();
    }

    let baseData: DeviceReading[] = [];

    if (userSelectedParameterId !== 0) {
      baseData = deviceSensorData.readings.filter(
        d => d.parameterDriverId === userSelectedParameterId
      );
    }

    let interval: number = 5 * 60 * 1000;
    if (userSelectedTimeRange === '12h') {
      interval = 5 * 60 * 1000;
    } else if (userSelectedTimeRange === '24h') {
      interval = 1 * 60 * 60 * 1000;
    } else if (
      userSelectedTimeRange === '7d' ||
      userSelectedTimeRange === '30d'
    ) {
      interval = 1 * 24 * 60 * 60 * 1000;
    }else if (userSelectedTimeRange === "custom") {
      const diffHours = getDifferenceInHours(new Date(customFromDate), new Date(customToDate));

      if (diffHours <= 12) {
        interval = 5 * 60 * 1000;                 // 5 minutes
      }
      else if (diffHours <= 24) {
        interval = 1 * 60 * 60 * 1000;            // 1 hour
      }
      else {
        interval = 24 * 60 * 60 * 1000;           // 1 day
      }
    }

    // Single parameter selected
    if (userSelectedParameterId2 === 0) {
      setChartData(baseData.length !== 0 ? fillMissingIntervals(baseData, interval) : []);
      return;
    }

    // Two parameters selected
    const secondData = deviceSensorData.readings.filter(
      d => d.parameterDriverId === userSelectedParameterId2
    );

    if (baseData.length === 0 && secondData.length === 0) {
      setChartData([]);
      return;
    } else if (baseData.length !== 0 && secondData.length === 0) {
      setChartData(fillMissingIntervals(baseData, interval));
      return;
    } else if (baseData.length === 0 && secondData.length !== 0) {
      setChartData(fillMissingIntervals(secondData, interval));
      return;
    }

    // Both parameters have data: fill against common bounds so the two series
    // share identical bucket slots, then merge them positionally by index.
    const allTimes = [...baseData, ...secondData].map(d =>
      new Date(d.recordedAt).getTime()
    );
    const bounds = {
      startTime: Math.min(...allTimes),
      endTime: Math.max(...allTimes),
    };
    const filledBase = fillMissingIntervals(baseData, interval, bounds);
    const filledSecond = fillMissingIntervals(secondData, interval, bounds);

    const merged = filledBase.map((d, i) => {
      const match = filledSecond[i];
      return {
        ...d,
        parameterValue2: match?.parameterValue ?? null,
        parameterName2: match?.parameterName ?? null,
        parameterDisplayName2: match?.parameterDisplayName ?? null,
        preferredUnitName2: match?.preferredUnitName ?? null,
        subIndex2: match?.subIndex ?? null,
      };
    });
    setChartData(merged);
  }, [
    userSelectedParameterId,
    userSelectedParameterId2,
    deviceSensorData,
    userSelectedTimeRange,
  ]);

  const getMainPollutantName = (recordedAt: string) => {
    const targetTime = new Date(recordedAt).getTime();
    const readings = deviceSensorData?.readings?.filter(
      d => new Date(d.recordedAt).getTime() === targetTime
    );
    if (!readings || readings.length === 0) return '';
    const maxItem = readings.reduce((prev, current) =>
      current.subIndex > prev.subIndex ? current : prev
    );
    return maxItem?.parameterName || '';
  };

  // Prepare Highcharts options
  const chartOptions = useMemo((): Highcharts.Options => {
    if (!chartData || chartData.length === 0 || !chartData[0]) {
      return {
        title: { text: '' },
        series: [],
      };
    }

    // Calculate Y range and detect if all Y values are identical
    const yValues = chartData.map(d => d.parameterValue).filter(v => v != null);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);
    const allSame = yMin === yMax;
    const hasSinglePoint = chartData.length === 1;

    const seriesArray: Highcharts.SeriesOptionsType[] = [
      {
        type: 'line',
        name: chartData[0]?.parameterName || 'Parameter 1',
        data: chartData.map(d => ({
          x: new Date(d.recordedAt).getTime(),
          y: d.parameterValue,
          color: !ShowSecondLine
            ? GetParameterColor(d.parameterValue, d.subIndex, d.parameterName)
            : '#005EE5',
        })),
        color: !ShowSecondLine
          ? allSame
            ? GetParameterColor(
                chartData[0].parameterValue,
                chartData[0].subIndex,
                chartData[0].parameterName
              )
            : {
                linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
                stops: chartData.map((d, i) => [
                  i / (chartData.length - 1),
                  GetParameterColor(
                    d.parameterValue,
                    d.subIndex,
                    d.parameterName
                  ),
                ]),
              }
          : '#005EE5',
        colorByPoint: false,
        lineWidth: hasSinglePoint ? 0 : 3, // hide line when there's only one point
        marker: {
          // When there is only a single point, show the marker so the value is visible
          enabled: hasSinglePoint,
          radius: hasSinglePoint ? 6 : 0,
          states: {
            hover: {
              enabled: true,
              radius: 8,
              lineWidth: 1.5,
              lineColor: '#fff',
            },
          },
        },
        animation: {
          duration: 1500,
        },
        yAxis: 0,
        connectNulls: true,
        zones: undefined,
      } as Highcharts.SeriesLineOptions,
    ];

    if (ShowSecondLine && chartData[0]?.parameterValue2 !== undefined) {
      const series2Data = chartData.map(d => ({
        x: new Date(d.recordedAt).getTime(),
        y: d.parameterValue2,
        color: '#00C8E6',
      }));

      seriesArray.push({
        type: 'line',
        name: parametersForDevice.find(p => p.driverID == userSelectedParameterId2)?.parameterName || 'Parameter 2',
        data: series2Data,
        color: '#00C8E6',
        lineWidth: 3,
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 8,
              lineWidth: 1.5,
              lineColor: '#fff',
            },
          },
        },
        yAxis: 1,
        connectNulls: true,
        animation: {
          duration: 1500,
        },
      } as Highcharts.SeriesLineOptions);
    }

    return {
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        animation: true,
        style: {
          fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
        },
        spacingLeft: 0,
        spacingRight: ShowSecondLine ? 0 : 5,
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        type: 'datetime',
        lineWidth: 1,
        lineColor: theme == 'light' ? '#E9EBF1' : '#4C5C68',
        tickWidth: 1,
        tickColor: theme == 'light' ? '#E9EBF1' : '#4C5C68',
        tickAmount: 4,
        crosshair: {
          width: 1,
          color: '#9291A5',
          dashStyle: 'Dash',
        },
        labels: {
          style: {
            color: theme == 'light' ? '#363636' : '#ffffff',
            fontSize: '12px',
            fontWeight: '400',
            fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
          },
          formatter: function () {
            const date = new Date(this.value);
            if (
              userSelectedTimeRange === '12h' ||
              userSelectedTimeRange === '24h'
            ) {
              return date.toLocaleTimeString('en-US', {
                timeZone: timeZone,
                hour: '2-digit',
                minute: '2-digit',
              });
            } else if (userSelectedTimeRange === '7d') {
              return date.toLocaleDateString('en-US', {
                timeZone: timeZone,
                month: 'short',
                day: 'numeric',
              });
            } else if (userSelectedTimeRange === '30d') {
              return date.toLocaleDateString('en-US', {
                timeZone: timeZone,
                month: 'short',
                day: 'numeric',
              });
            } else if (userSelectedTimeRange === 'custom') {
              const diffHours = getDifferenceInHours(new Date(customFromDate), new Date(customToDate));
              if (diffHours <= 12 || diffHours <= 24) {
                return date.toLocaleTimeString('en-US', {
                  timeZone: timeZone,
                  hour: '2-digit',
                  minute: '2-digit',
                });
              }else {
                return date.toLocaleDateString('en-US', {
                  timeZone: timeZone,
                  month: 'short',
                  day: 'numeric',
                });
              }
            }
            return '';
          },
        },
        gridLineWidth: 0,
      },
      yAxis: [
        {
          title: {
            text: `${chartData[0]?.parameterDisplayName?.length > 20 ? chartData[0]?.parameterDisplayName?.slice(0, 20) + '...' : chartData[0]?.parameterDisplayName || ''}${chartData[0]?.preferredUnitName ? ` (${chartData[0]?.preferredUnitName})` : ''}`,
            style: {
              color: theme == 'light' ? '#4C5C68' : '#ffffff',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: '400',
              fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
            },
          },
          labels: {
            style: {
              color: theme == 'light' ? '#363636' : '#ffffff',
              fontSize: isMobile ? '10px' : '12px',
              fontWeight: '400',
              fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
            },
          },
          gridLineColor: '#E9EBF1',
          lineWidth: 1,
          lineColor: theme == 'light' ? '#E9EBF1' : '#4C5C68',
          softMin: allSame ? (yMin < 1 ? yMin - yMin : yMin - 1) : undefined,
          softMax: allSame ? yMax + 1 : undefined,
        },
        ...(ShowSecondLine && chartData[0]?.parameterValue2 !== undefined
          ? [
              {
                title: {
                  text: `${chartData[0]?.parameterDisplayName2?.length > 20 ? chartData[0]?.parameterDisplayName2?.slice(0, 20) + '...' : chartData[0]?.parameterDisplayName2 || ''}${chartData[0]?.preferredUnitName2 ? ` (${chartData[0]?.preferredUnitName2})` : ''}`,
                  style: {
                    color: theme == 'light' ? '#4C5C68' : '#ffffff',
                    fontSize: isMobile ? '10px' : '12px',
                    fontWeight: '300',
                    fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
                  },
                },
                labels: {
                  style: {
                    color: theme == 'light' ? '#363636' : '#ffffff',
                    fontSize: isMobile ? '10px' : '12px',
                    fontWeight: '500',
                    fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
                  },
                },
                opposite: true,
                gridLineWidth: 0,
                lineWidth: 1,
                lineColor: theme == 'light' ? '#E9EBF1' : '#4C5C68',
              },
            ]
          : []),
      ],
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: 'transparent',
        borderWidth: 0,
        shadow: false,
        padding: 0,
        style: {
          pointerEvents: 'none',
        },
        positioner: function (labelWidth, labelHeight, point) {
          const chart = this.chart;
          const plotLeft = chart.plotLeft;
          const plotTop = chart.plotTop;
          const plotWidth = chart.plotWidth;
          const plotHeight = chart.plotHeight;
          let x = point.plotX + plotLeft + 10;
          let y = point.plotY + plotTop - labelHeight / 2;
          // Shift left if overflowing right edge
          if (x + labelWidth > plotLeft + plotWidth) {
            x = point.plotX + plotLeft - labelWidth - 10;
          }
          // Keep within left bound
          if (x < plotLeft) { x = plotLeft; }
          // Keep within top bound
          if (y < plotTop) { y = plotTop; }
          // Keep within bottom bound
          if (y + labelHeight > plotTop + plotHeight) {
            y = plotTop + plotHeight - labelHeight;
          }
          return { x, y };
        },
        formatter: function () {
          const points = this.points;
          if (!points || points.length === 0) return '';

          const point = points[0];
          // Find data by matching timestamp (x value) with recordedAt
          const pointTimestamp = (point as any).x;
          const data = chartData.find(d => new Date(d.recordedAt).getTime() === pointTimestamp);
          if (!data) return '';

          const formatValue = (val: any, paramName: string) => {
            if (val == null) return 'N/A';
            if (paramName === 'AQI') {
              return Math.round(val);
            }
            return Number.isInteger(val) ? val : val < 0.01 ? val.toFixed(3) : val.toFixed(2);
          };

          const param1Color =
            userSelectedParameterId2 === 0
              ? GetParameterColor(
                  data.parameterValue,
                  data.subIndex,
                  data.parameterName
                )
              : '#005EE5';

          const bgColor = theme === 'light' ? '#fff' : '#333333';
          const textColor = theme === 'light' ? '#333' : '#f0f0f0';
          const subTextColor = theme === 'light' ? '#6b7280' : '#9ca3af';
          const borderColor = theme === 'light' ? '#E9EBF1' : '#555';

          let tooltipHTML = `<div style="font-size:11px; min-width:min-content; max-width:340px; line-height:1.5; white-space:normal; word-wrap:break-word; overflow-wrap:break-word; font-family:'DM Sans','Inter',system-ui,sans-serif; background:${bgColor}; color:${textColor}; padding:${isMobile ? '8px' : '12px'}; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.15);">`;

          const tooltipDate = new Date(data.recordedAt);
          const timePart = tooltipDate.toLocaleTimeString('en-US', { timeZone: timeZone, hour: '2-digit', minute: '2-digit', hour12: true });
          const datePart = tooltipDate.toLocaleDateString('en-US', { timeZone: timeZone, year: 'numeric', month: 'short', day: 'numeric' });
          const tzAbbr = getTimezoneAbbreviation(timeZone);
          tooltipHTML += `<div style="margin-bottom:4px; font-weight:600; white-space:nowrap;">${timePart} ${tzAbbr}, ${datePart}</div>`;

          tooltipHTML += `<div style="color: ${param1Color}; margin-bottom:4px; font-size: ${isMobile ? '12px' : '14px'}; font-weight: 500;">${data.parameterName}: ${formatValue(data.parameterValue, data.parameterName)} ${data.preferredUnitName || ''}</div>`;

          if (data.parameterValue2 != null) {
            tooltipHTML += `<div style="color: #00C8E6; margin-bottom:4px; font-size: ${isMobile ? '12px' : '14px'}; font-weight: 500;">${data.parameterName2}: ${formatValue(data.parameterValue2, data.parameterName2)} ${data.preferredUnitName2 || ''}</div>`;
          }

          if (data.parameterName === 'AQI' || data.parameterName2 === 'AQI') {
            const mainPollutant = getMainPollutantName(data.recordedAt);
            if (mainPollutant) {
              tooltipHTML += `<div style="margin-bottom:4px; font-weight: 300; color: ${textColor};">Main pollutant: ${mainPollutant}</div>`;
            }
          }

          if (userSelectedParameterId2 === 0) {
            const statusText =
              GetStatusText(
                data.parameterValue,
                data.subIndex,
                data.parameterName
              ) || 'Not Available';
            tooltipHTML += `<div style="font-size:11px; font-weight:400; color:${subTextColor}; border-top:1px solid ${borderColor}; padding-top:0.5rem; margin-top:4px;">${statusText}</div>`;
          }

          tooltipHTML += `</div>`;
          return tooltipHTML;
        },
      },
      legend: {
        enabled: ShowSecondLine ? true : false,
        verticalAlign: 'top',
        events: {
          itemClick: function () {
            return false; // Prevent item click
          },
        },
        itemStyle: {
          color: theme == 'light' ? '#363636' : '#ffffff',
          fontSize: '12px',
          fontWeight: '400',
          fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
        },
        itemHoverStyle: {
          fontWeight: '500',
        },
      },
      plotOptions: {
        line: {
          animation: true,
          marker: {
            enabled: false,
          },
        },
        series: {
          states: {
            hover: {
              lineWidthPlus: 0,
            },
          },
        },
      },
      series: seriesArray,
    };
  }, [
    chartData,
    ShowSecondLine,
    userSelectedParameterId,
    userSelectedParameterId2,
    userSelectedTimeRange,
    isMobile,
    deviceSensorData,
  ]);

  const handleTimeRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserSelectedTimeRange(e.target.value);
    if (e.target.value !== 'custom') setShowTimeRangeDropdown(false);
    // API call will be triggered by useEffect when userSelectedTimeRange changes
  };

  // Handle parameter click
  const handleParameterClick = (parameterId: number) => {
    // Case 1: clicked the first param again → deselect
    if (userSelectedParameterId === parameterId && userSelectedParameterId2 !== 0) {
      setUserSelectedParameterId(userSelectedParameterId2);
      setUserSelectedParameterId2(0);
      setShowSecondLine(false);
      return;
    }

    // Case 2: clicked the second param again → deselect second only
    if (userSelectedParameterId2 === parameterId) {
      setUserSelectedParameterId2(0);
      setShowSecondLine(false);
      return;
    }

    // Case 3: no second param selected → set as second
    if (parameterId !== userSelectedParameterId && userSelectedParameterId2 === 0) {
      setUserSelectedParameterId2(parameterId);
      setShowSecondLine(true);
      return;
    }

    // Case 4: both already selected → replace second with new
    if (
      parameterId !== userSelectedParameterId &&
      parameterId !== userSelectedParameterId2
    ) {
      setUserSelectedParameterId(userSelectedParameterId2);
      setUserSelectedParameterId2(parameterId);
      setShowSecondLine(true);
      return;
    }
  };

  const getMainPollutantNameForMetrics = () => {
    if (!liveDeviceData?.parameters) return '';
    const maxItem = Object.values(liveDeviceData.parameters).reduce(
      (prev, current) => (current.subIndex > prev.subIndex ? current : prev)
    );
    return maxItem?.parameterName || '';
  };

  return (
    <>
      <div className="device-overview-container scrollbar">
        {/* Left Panel: AQI & Pollutants Metrics */}
        {!hidePollutantsCard && <div className="device-overview-left-panel w-full lg:w-[20%]">
          <div className="device-overview-left-panel-header">
            <h3 className="overview-left-panel-title">AQI & Pollutants</h3>
            {liveDeviceData.isActive && (
              <div className="live-data-badge">
                <span className="w-3 h-3 rounded-full bg-[var(--color-terms-link)] inline-block animate-pulse"></span>{' '}
                Live Data
              </div>
            )}
          </div>
          <div
            className={`${parametersForDevice && parametersForDevice.length > 12 ? 'livedata-container pr-1.5' : ''} md:grid flex lg:grid-cols-3 md:grid-cols-7 sm:gap-x-4 gap-x-2 gap-y-2 overflow-auto`}
          >
            {liveDeviceData?.parameters &&
              Object.values(liveDeviceData.parameters)
                .sort((a, b) => {
                  if (a.parameterName === 'AQI') return -1;
                  if (b.parameterName === 'AQI') return 1;
                  if (a.parameterName === getMainPollutantNameForMetrics())
                    return -1;
                  if (b.parameterName === getMainPollutantNameForMetrics())
                    return 1;
                  return 0;
                })
                .map((LiveDataParameter: ParameterDto, index) => {
                  const isMainPollutant =
                    LiveDataParameter.parameterName !== 'AQI' &&
                    LiveDataParameter.parameterName ===
                      getMainPollutantNameForMetrics();
                  const parameterValue =
                    LiveDataParameter.parameterName === 'AQI'
                      ? LiveDataParameter.value != null
                        ? Math.round(LiveDataParameter.value)
                        : '--'
                      : LiveDataParameter.value != null
                        ? Number.isInteger(LiveDataParameter.value)
                          ? LiveDataParameter.value
                          : LiveDataParameter.value.toFixed(2)
                        : '--';
                  const parameterUnit =
                    LiveDataParameter.parameterName === 'AQI'
                      ? aqiStandardId === 1
                        ? 'USA'
                        : aqiStandardId === 2
                          ? 'UAE'
                          : aqiStandardId === 3
                            ? 'INDIA'
                            : 'N/A'
                      : parametersForDevice?.find(
                          parameter =>
                            parameter.driverID ===
                            LiveDataParameter.parameterDriverId
                        )?.unit;
                  return (
                    <div className="cursor-pointer pollutant-gauge" key={index}>
                      <CircularProgressbarWithChildren
                        maxValue={HelperFunctions.GetMaximumValueForParameter(
                          LiveDataParameter.parameterName
                        )}
                        styles={buildStyles({
                          pathColor: GetParameterColor(
                            LiveDataParameter.value,
                            LiveDataParameter.subIndex,
                            LiveDataParameter.parameterName
                          ),
                          trailColor: theme == "light" ? '#E9EBF1' : '#16140E',
                          strokeLinecap: 'butt',
                          pathTransition: 'stroke-dashoffset 0.5s ease 0s',
                        })}
                        className="custom-progress"
                        strokeWidth={6}
                        value={LiveDataParameter.value}
                      >
                        <div className="gauge-content">
                          <p
                            className="pollutant-name"
                            style={{
                              color: isMainPollutant ? '#214CA8' : '',
                              fontStyle: isMainPollutant ? 'italic' : '',
                            }}
                          >
                            {LiveDataParameter.parameterName.length > 5
                              ? LiveDataParameter.parameterName.slice(0, 4) +
                                '...'
                              : LiveDataParameter.parameterName}
                          </p>
                          <p className="pollutant-value">{parameterValue}</p>
                          <p
                            className="pollutant-unit"
                            style={{
                              color:
                                LiveDataParameter.parameterName === 'AQI'
                                  ? '#214CA8'
                                  : '',
                              fontWeight:
                                LiveDataParameter.parameterName === 'AQI'
                                  ? '700'
                                  : '',
                            }}
                          >
                            {parameterUnit}
                          </p>
                        </div>
                      </CircularProgressbarWithChildren>
                    </div>
                  );
                })}
          </div>
          <div className="main-pollutant-info">
            <span className="main-pollutant-info-icon"></span>
            <span>This color shows the AQI standard and the main pollutant affecting the AQI.</span>
          </div>
        </div>}
        <div className={`device-overview-right-panel w-full ${hidePollutantsCard ? '' : 'lg:w-[80%]'}`}>
          <div className="trend-analysis">
            <div className={`trend-header ${ShowSecondLine ? 'mb-4' : 'mb-6'}`}>
              <div className="chart-title-section">
                <h3 className="trend-title">Detailed Insights</h3>
              </div>
              <div className="trend-controls">
                {isMobile ? (
                  <button
                    onClick={() =>
                      setShowTimeRangeDropdown(!showTimeRangeDropdown)
                    }
                    className="flex gap-2 items-center device-dropdown-select"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/SVGIcons/CalenderIcon.svg`}
                      alt="calendar"
                      className="w-4 h-4"
                    />
                    <span>
                      {
                        timeRanges.find(
                          range => range.value === userSelectedTimeRange
                        )?.label
                      }
                    </span>
                    <ChevronDown className="w-5 h-5 ml-auto" />
                  </button>
                ) : (
                  <div className="dropdown-container">
                    <div
                      className="dropdown-label"
                      onClick={() =>
                        setShowTimeRangeDropdown(!showTimeRangeDropdown)
                      }
                    >
                      <div className="dropdown-label-content">
                        <img
                          src={`${process.env.PUBLIC_URL}/SVGIcons/CalenderIcon.svg`}
                          alt="arrow"
                          className="mr-2"
                        />
                        <p>
                          {
                            timeRanges.find(
                              range => range.value === userSelectedTimeRange
                            )?.label
                          }
                        </p>
                      </div>
                      <ChevronDown className="w-5 h-5 text-[var(--color-text)]" />
                    </div>
                    {showTimeRangeDropdown && (
                      <div className='dropdown-options'>
                        {timeRanges.map(range => (
                          <label key={range.value} className="dropdown-option">
                            <input
                              type="radio"
                              name="timeRange"
                              value={range.value}
                              checked={userSelectedTimeRange === range.value}
                              onChange={handleTimeRangeChange}
                              className="radio-input"
                            />
                            <span className="radio-label">{range.label}</span>
                          </label>
                        ))}

                        {userSelectedTimeRange == "custom" && (
                          <div className="custom-time-range-container">
                            <div className="custom-time-range-inputs">
                              <div className="custom-date-field-wrapper">
                                <label htmlFor="customFromDate" className="custom-date-label">From:</label>
                                <input
                                  type="date"
                                  name="timeRange"
                                  id="customFromDate"
                                  value={customFromDate.split('T')[0]}
                                  onClick={(e) => e.currentTarget.showPicker()}
                                  onChange={(e) => setCustomFromDate(e.target.value)}
                                  className="custom-date-input"
                                />
                              </div>
                              <div className="custom-date-field-wrapper">
                                <label htmlFor="customToDate" className="custom-date-label">To:</label>
                                <input
                                  type="date"
                                  name="timeRange"
                                  id="customToDate"
                                  value={customToDate.split('T')[0]}
                                  onClick={(e) => e.currentTarget.showPicker()}
                                  onChange={(e) => setCustomToDate(e.target.value)}
                                  className="custom-date-input"
                                />
                              </div>
                            </div>
                            {customDateError && (
                              <div className="custom-date-error-container">
                                <AlertCircle className="w-4 h-4 text-red-500" />
                                <p>{customDateError}</p>
                              </div>
                            )}
                            <div className="custom-date-buttons">
                              <button onClick={handleCustomDateCancel} className="custom-date-cancel-button">Cancel</button>
                              <button onClick={handleCustomDateChange} className="custom-date-ok-button">Apply</button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {loading ? (
              <div className="loading-container device-overview-chart-container">
                <div className="h-8 w-8 border-b-2 border-[var(--gradient-button-left-color)] mb-4 animate-spin rounded-full"></div>
                <p className="text-base text-[var(--color-sub-title-text)]">
                  Loading sensor data...
                </p>
              </div>
            ) : (
              <>
                {deviceSensorData !== null ? (
                    <div className="device-overview-chart-container" key={innerWidth}>
                      {chartData.length > 0 ? (
                        <HighchartsReact
                          highcharts={Highcharts}
                          options={chartOptions}
                          containerProps={{
                            style: { flex: '1 1 0%' },
                          }}
                        />
                      ) : (
                        <div className="error-container h-full">
                          <p>No sensor data is available for the selected parameters. Try choosing a different parameters.</p>
                        </div>
                      )}

                      <div className="metric-selectors">
                        {parametersForDevice &&
                          parametersForDevice
                            .sort((a, _b) => (a.parameterName === 'AQI' ? -1 : 1))
                            .map(parameter => (
                              <button
                                key={parameter.driverID}
                                className={`metric-button ${userSelectedParameterId === parameter.driverID || userSelectedParameterId2 === parameter.driverID ? 'active' : ''}`}
                                onClick={() =>
                                  handleParameterClick(parameter.driverID)
                                }
                              >
                                {parameter.parameterName === 'Temperature'
                                  ? 'Temp'
                                  : parameter.parameterName === 'Humidity'
                                    ? 'Hum'
                                    : parameter.parameterName}
                              </button>
                            ))}
                      </div>
                    </div>
                ) : (
                  <div className="error-container device-overview-chart-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem' }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '1rem' }}>
                      <path d="M3 3v18h18" />
                      <path d="M7 16l4-4 4 4 5-5" />
                    </svg>
                    <p style={{ fontSize: '1rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>No Data Available</p>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>There is no recorded data for the selected time period. Please try selecting a different date range.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Time Range Selection Modal */}
      {isMobile && showTimeRangeDropdown && (
        <div
          className="mobile-modal-overlay"
          onClick={() => setShowTimeRangeDropdown(false)}
        >
          <div className="mobile-modal" onClick={e => e.stopPropagation()}>
            <div className="mobile-modal-header">
              <h3 className="mobile-modal-title">Select Time Range</h3>
              <X
                className="w-5 h-5 text-[var(--color-text)]"
                onClick={() => setShowTimeRangeDropdown(false)}
              />
            </div>
            <div className="mobile-modal-content">
              {timeRanges.map(range => (
                <label key={range.value} className="mobile-modal-option">
                  <input
                    type="radio"
                    name="timeRange"
                    value={range.value}
                    checked={userSelectedTimeRange === range.value}
                    onChange={handleTimeRangeChange}
                    className="mobile-modal-radio"
                  />
                  <span className="mobile-modal-label">{range.label}</span>
                </label>
              ))}
              {userSelectedTimeRange === "custom" && (
                <div className="custom-time-range-container">
                  <div className="custom-time-range-inputs">
                    <div className="custom-date-field-wrapper">
                      <label htmlFor="customFromDate" className="custom-date-label">From:</label>
                      <input
                        type="date"
                        name="timeRange"
                        id="customFromDate"
                        value={customFromDate.split('T')[0]}
                        onClick={(e) => e.currentTarget.showPicker()}
                        onChange={(e) => setCustomFromDate(e.target.value)}
                        className="custom-date-input"
                      />
                    </div>
                    <div className="custom-date-field-wrapper">
                      <label htmlFor="customToDate" className="custom-date-label">To:</label>
                      <input
                        type="date"
                        name="timeRange"
                        id="customToDate"
                        value={customToDate.split('T')[0]}
                        onClick={(e) => e.currentTarget.showPicker()}
                        onChange={(e) => setCustomToDate(e.target.value)}
                        className="custom-date-input"
                      />
                    </div>
                  </div>
                  {customDateError && (
                    <div className="custom-date-error-container">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      <p>{customDateError}</p>
                    </div>
                  )}
                  <div className="custom-date-buttons">
                    <button onClick={handleCustomDateCancel} className="custom-date-cancel-button">Cancel</button>
                    <button onClick={handleCustomDateChange} className="custom-date-ok-button">Apply</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DeviceOverviewHighcharts;
